import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ScrollProgress from '../components/ScrollProgress';
import { useLanguage } from '../contexts/LanguageContext';
import { mockData } from '../data/mock';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Building2,
  Users,
  CheckCircle2,
  Phone,
  Mail,
  Send,
} from 'lucide-react';

const BGCoursesPage = () => {
  const { t, language } = useLanguage();

  const benefits = [
    language === 'de' ? 'DGUV 304-001 zertifiziert' : 'DGUV 304-001 certified',
    language === 'de' ? 'BG Abrechnung möglich' : 'BG billing possible',
    language === 'de' ? 'Flexible Terminplanung' : 'Flexible scheduling',
    language === 'de' ? 'Inhouse-Schulungen möglich' : 'In-house training available',
    language === 'de' ? 'Erfahrene Ausbilder' : 'Experienced instructors',
    language === 'de' ? 'Praxisnahe Inhalte' : 'Practical content',
  ];

  return (
    <>
      <ScrollProgress />
      <Header />
      <main className="pt-24">
        {/* Hero */}
        <section className="bg-gradient-hero py-20">
          <div className="container-custom">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 bg-red-100 text-red-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Building2 className="w-4 h-4" />
                <span>{language === 'de' ? 'Für Unternehmen' : 'For Companies'}</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-neutral-900 mb-4">
                {t('bgCourses.pageTitle')}
              </h1>
              <p className="text-xl text-neutral-600">
                {t('bgCourses.pageDescription')}
              </p>
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="py-16 bg-white">
          <div className="container-custom">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl font-bold text-neutral-900 mb-6">
                  {language === 'de'
                    ? 'Warum Erste-Hilfe Fokus für Ihr Unternehmen?'
                    : 'Why Erste-Hilfe Fokus for your company?'}
                </h2>
                <p className="text-neutral-600 mb-4">
                  {language === 'de'
                    ? 'Für Betriebe bieten wir Ihnen einen erfahrenen, freiberuflichen Dozenten, der direkt zu Ihnen ins Unternehmen kommt. Unser Dozent arbeitet in Kooperation mit Med1Plus zusammen und führt professionelle Erste-Hilfe-Schulungen nach DGUV Vorschrift 1 durch.'
                    : 'For companies, we offer an experienced freelance instructor who comes directly to your premises. Our instructor works in cooperation with Med1Plus and conducts professional first aid training according to DGUV Regulation 1.'}
                </p>
                <p className="text-neutral-600 mb-6">
                  {language === 'de'
                    ? 'Unsere Kurse entsprechen allen gesetzlichen Anforderungen und können direkt über die Berufsgenossenschaft abgerechnet werden.'
                    : 'Our courses meet all legal requirements and can be billed directly through the professional association.'}
                </p>

                {/* Med1Plus Partner Info */}
                <div className="flex items-center gap-4 bg-neutral-100 p-4 rounded-xl mb-8">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_erstehelfer/artifacts/1dk7ec8k_cropped-med1plus-Logo-gruen.png" 
                    alt="Med1Plus Logo" 
                    className="h-8 w-auto"
                  />
                  <p className="text-sm text-neutral-600">
                    {language === 'de'
                      ? 'In Kooperation mit Med1Plus – Ihr Partner für betriebliche Erste-Hilfe-Ausbildung'
                      : 'In cooperation with Med1Plus – Your partner for workplace first aid training'}
                  </p>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  {benefits.map((benefit, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <CheckCircle2 className="w-5 h-5 text-red-600 flex-shrink-0" />
                      <span className="text-neutral-700 font-medium">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-neutral-50 rounded-2xl p-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 bg-red-100 rounded-xl flex items-center justify-center">
                    <Users className="w-8 h-8 text-red-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-neutral-900">
                      {language === 'de' ? 'Betrieblicher Ersthelfer' : 'Workplace First Aider'}
                    </h3>
                    <p className="text-neutral-600">
                      {language === 'de' ? 'Gemäß DGUV Vorschrift 1' : 'According to DGUV Regulation 1'}
                    </p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="bg-white p-4 rounded-xl">
                    <p className="text-sm text-neutral-500">
                      {language === 'de' ? 'Kursdauer' : 'Course Duration'}
                    </p>
                    <p className="font-semibold text-neutral-900">9 UE (7,5 Stunden)</p>
                  </div>
                  <div className="bg-white p-4 rounded-xl">
                    <p className="text-sm text-neutral-500">
                      {language === 'de' ? 'Gruppengröße' : 'Group Size'}
                    </p>
                    <p className="font-semibold text-neutral-900">
                      {language === 'de' ? 'Bis zu 20 Teilnehmer' : 'Up to 20 participants'}
                    </p>
                  </div>
                  <div className="bg-white p-4 rounded-xl">
                    <p className="text-sm text-neutral-500">
                      {language === 'de' ? 'Abrechnung' : 'Billing'}
                    </p>
                    <p className="font-semibold text-neutral-900">
                      {language === 'de' ? 'Direkt über BG möglich' : 'Direct BG billing possible'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="py-16 bg-neutral-50">
          <div className="container-custom">
            <div className="max-w-2xl mx-auto">
              <div className="text-center mb-10">
                <h2 className="text-3xl font-bold text-neutral-900 mb-4">
                  {t('bgCourses.title')}
                </h2>
                <p className="text-neutral-600">{t('bgCourses.description')}</p>
              </div>

              <form 
                action="https://api.web3forms.com/submit"
                method="POST"
                className="bg-white rounded-2xl p-8 shadow-lg"
              >
                {/* Web3Forms Configuration */}
                <input type="hidden" name="access_key" value="d8528687-1ecb-4add-b8b0-ca987b425652" />
                <input type="hidden" name="subject" value="BG-Kurs Anfrage - Erste-Hilfe Fokus" />
                <input type="hidden" name="redirect" value="https://erstehilfe-fokus.de/#/bg-kurse?success=true" />
                
                <div className="grid sm:grid-cols-2 gap-6 mb-6">
                  <div>
                    <Label htmlFor="companyName">
                      {language === 'de' ? 'Firmenname *' : 'Company Name *'}
                    </Label>
                    <Input
                      id="companyName"
                      name="companyName"
                      required
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label htmlFor="contactPerson">
                      {language === 'de' ? 'Ansprechpartner *' : 'Contact Person *'}
                    </Label>
                    <Input
                      id="contactPerson"
                      name="contactPerson"
                      required
                      className="mt-2"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-6 mb-6">
                  <div>
                    <Label htmlFor="email">{t('contact.form.email')} *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">{t('contact.phone')} *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      required
                      className="mt-2"
                    />
                  </div>
                </div>

                <div className="mb-6">
                  <Label htmlFor="employees">
                    {language === 'de'
                      ? 'Anzahl der zu schulenden Mitarbeiter'
                      : 'Number of employees to train'}
                  </Label>
                  <Input
                    id="employees"
                    name="employees"
                    type="number"
                    className="mt-2"
                  />
                </div>

                <div className="mb-6">
                  <Label htmlFor="message">{t('contact.form.message')}</Label>
                  <Textarea
                    id="message"
                    name="message"
                    rows={4}
                    className="mt-2"
                    placeholder={
                      language === 'de'
                        ? 'Gewünschter Zeitraum, besondere Anforderungen, etc.'
                        : 'Preferred time period, special requirements, etc.'
                    }
                  />
                </div>

                <button
                  type="submit"
                  className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-3 px-6 rounded-xl transition-colors flex items-center justify-center gap-2 group"
                >
                  <span>{t('bgCourses.cta')}</span>
                  <Send className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>

                {/* Info text */}
                <p className="mt-4 text-sm text-neutral-500 text-center">
                  {language === 'de' 
                    ? 'Ihre Anfrage wird an info@erstehilfe-fokus.de gesendet.'
                    : 'Your request will be sent to info@erstehilfe-fokus.de.'}
                </p>
              </form>

              {/* Direct Contact */}
              <div className="mt-8 text-center">
                <p className="text-neutral-600 mb-4">
                  {language === 'de'
                    ? 'Oder kontaktieren Sie uns direkt:'
                    : 'Or contact us directly:'}
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <a
                    href={`tel:${mockData.company.phone}`}
                    className="inline-flex items-center justify-center gap-2 bg-white px-6 py-3 rounded-xl border border-neutral-200 hover:border-red-600 transition-colors"
                  >
                    <Phone className="w-5 h-5 text-red-600" />
                    <span className="font-medium">0159 0226 8861</span>
                  </a>
                  <a
                    href={`mailto:${mockData.company.emailInfo}`}
                    className="inline-flex items-center justify-center gap-2 bg-white px-6 py-3 rounded-xl border border-neutral-200 hover:border-red-600 transition-colors"
                  >
                    <Mail className="w-5 h-5 text-red-600" />
                    <span className="font-medium">{mockData.company.emailInfo}</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default BGCoursesPage;
